import React from 'react';
import styled, { DefaultTheme, css } from 'styled-components';

import { solve } from '../../scripts/common';
import { Cross, Directional, LineAlign, ItemAlign } from '../../scripts/types';

const pads = {
  none: '0px',
  xsmall: '5px',
  small: '10px',
  medium: '20px',
  large: '30px',
  xlarge: '50px',
} as const;
type Pad = keyof typeof pads;

export const colors = (theme: DefaultTheme) =>
  ({
    light: css`
      background-color: ${theme.pallete.primary.light};
    `,
    main: css`
      background-color: ${theme.pallete.primary.main};
    `,
    black: css`
      background-color: black;
    `,
    white: css`
      background-color: white;
    `,
    dark: css`
      background-color: ${theme.pallete.border.main};
    `,
    gradient: css`
      background-image: linear-gradient(
        172.14deg,
        ${theme.pallete.accent.main} 12.49%,
        ${theme.pallete.accent.dark} 198.93%
      );
    `,
    DC: css`
      background-color: #f0c7c7;
    `,
    CP: css`
      background-color: #9cdff4;
    `,
    CD: css`
      background-color: #cbfdd0;
    `,
    FUSE: css`
      background-color: #f5a085;
    `,
    DS: css`
      background-color: #e6ceec;
    `,
    MODEDARK: css`
      background-color: #282829;
    `,
  } as const);
type Color = keyof ReturnType<typeof colors>;

type StyledSingleCardProps = {
  color: Color;
  area: string;
  pad: Directional<Pad>;
  halign?: LineAlign;
  valign?: ItemAlign;
};
const StyledSingleCard = styled.div<StyledSingleCardProps>`
  display: grid;
  grid-template:
    'cardcontent' 1fr /
    auto;

  ${(props) => colors(props.theme)[props.color]};
  box-shadow: rgba(77, 124, 254, 0.08) 0em 0.4375em 0.625em;
  border-radius: 8px;

  width: 210px;
  height: 60px;

  grid-area: ${(props) => props.area};
  padding-left: ${(props) => pads[props.pad.left ?? 'none']};
  padding-right: ${(props) => pads[props.pad.right ?? 'none']};
  padding-top: ${(props) => pads[props.pad.top ?? 'none']};
  padding-bottom: ${(props) => pads[props.pad.bottom ?? 'none']};

  justify-content: ${(props) => props.halign ?? 'start'};
  align-items: ${(props) => props?.valign ?? 'stretch'};
`;

export type SingleCardProps = {
  children?: React.ReactNode;
  /**
   * Background color of the card.
   */
  color?: Color;
  /**
   * Grid area name. Use different areas for different grids. Defaults to
   * "singlecard".
   */
  area?: string;
  /**
   * Padding for all directions (top, bottom, left, right)
   */
  pad?: Pad;
  /**
   * Individual padding for each direction.
   */
  padAll?: Directional<Pad>;
  /**
   * Padding vertical and horizontal.
   */
  padCross?: Cross<Pad>;
  /**
   * Horizontal aligment of items.
   */
  halign?: LineAlign;
  /**
   * Vertical aligment of items.
   */
  valign?: ItemAlign;

  onClick?: () => void;
};

/**
 * This "single" card container can hold one card content, centralized. The usage
 * of this component, albeit a bit verbose, but highly readable will be:
 *
 * ```tsx
 *   <SingleCard color="light">
 *    <CardContent>
 *      <Title value="Corrente do PCS" />
 *      <Icon name="BoltCCircle" size="small" color="white" />
 *      <Quantity value={12.4} unit="current" color="white" size="large" />
 *    </CardContent>
 * </SingleCard>
 * ```
 */
export const SingleCard: React.FC<SingleCardProps> = ({
  color = 'main',
  area = 'singlecard',
  onClick = () => null,
  ...props
}: SingleCardProps) => {
  const { children, pad, padAll, padCross, halign, valign } = props;
  return (
    <StyledSingleCard
      color={color}
      area={area}
      pad={solve(pad, padCross, padAll)}
      halign={halign}
      valign={valign}
      onClick={onClick}>
      {children}
    </StyledSingleCard>
  );
};
